import React, { FC, useState, useContext, useEffect } from 'react';
import styled from '@emotion/styled';
import { useModal } from '@blueprint/ui';
import { useRouter } from 'next/router';

import { AlertPanel } from '../AlertPanel';
import { AdvertProvider } from 'shared/components/Adverts/AdvertProviderDfp';
import { AdvertSlotDfp } from 'shared/components/Adverts/AdvertSlotDfp';
import { FiltersModal } from '../FiltersModal';
import { MobileQuickSearchButton } from '../MobileQuickSearchButton';
import { SearchInitiation } from 'shared/components/SearchInitiation';
import { SeoLinksComponent } from '../SeoLinks';
import { ModalManager } from 'shared/components/ModalManager/ModalManager';
import {
    DFP_AD_UNIT,
    DfpTargeting,
    AD_UNIT_ROOTS,
    AD_UNIT_PAGES
} from '../../shared/components/Adverts/types';
import { SeoLinks } from '../SeoLinks/SeoLinks.types';
import { AlertCtaModal } from '../AlertCtaModal';
import { breakpoints } from '../common/constants';
import { SearchContext } from '../../search/SearchContext';

import {
    SearchResultsWrapper,
    DesktopSubHeaderWrapper,
    SideBar,
    InnerWrapper,
    StyledMobileQuickSearchBar,
    LocationWrapper,
    StickyQuickSearchBarWrapper,
    StickyQuickSearchBarDynamicShadow,
    SearchInputWrapper
} from './Layout.styles';
import { AlertPanelMobile } from 'search/AlertPanelMobile';
import { getTranslator } from '../../shared/i18n/getTranslator';
import searchTranslations from 'public/locales/en/search.json';
import { ModalTypes } from 'shared/components/ModalManager/ModalManager.types';
import { Links } from '../SearchContext';
import { LayoutVersion } from '../../constants';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';

const Sticky = styled.div`
    position: sticky;
    top: 0;
    width: 100%;
`;

const Main = styled.main`
    width: 100%;
`;

type SearchResultsLayout = {
    dfpAdTargeting: DfpTargeting;
    seoAccordions: SeoLinks[];
    numberOfRegularListings: number;
    pageNumber?: number;
    links: Links;
};

const t = getTranslator(searchTranslations);
const mobileModalUpdateResultsText = t('filters.updateResults');

const SearchResultsLayout: FC<SearchResultsLayout> = ({
    dfpAdTargeting,
    seoAccordions,
    children,
    pageNumber,
    links
}) => {
    const { state } = useContext(SearchContext);
    const isLoggedIn = useIsLoggedIn();

    const router = useRouter();
    const { route } = router;

    const [modalType, setModalType] = useState<ModalTypes | null>(null);
    const [isModalShowing, openModal, closeModal] = useModal();

    const openModalType = (type: ModalTypes) => {
        setModalType(type);
        openModal(null);
    };

    const closeModalType = () => {
        setModalType(null);
        closeModal();
    };

    const [adProviderVal, updateAdProviderVal] = useState({
        showPlaceHolderContent: false,
        reportMode: false,
        targeting: {
            ...dfpAdTargeting,
            signed_in_status: !!isLoggedIn ? 'signed in' : 'signed out'
        },
        pageNumber
    });

    useEffect(() => {
        updateAdProviderVal({
            showPlaceHolderContent: false,
            reportMode: false,
            targeting: {
                ...dfpAdTargeting,
                signed_in_status: !!isLoggedIn ? 'signed in' : 'signed out'
            },
            pageNumber
        });
    }, [dfpAdTargeting, isLoggedIn, pageNumber]);

    const { searchType } = state;
    const isForSaleSearch = searchType === 'for-sale';

    // Using the route to determine searchType as using the searchType value from context state results in a race condition with advert initialisation.
    const isForSaleSearchFromRouter = route.startsWith('/for-sale');

    const adUnitPage = isForSaleSearchFromRouter
        ? AD_UNIT_PAGES.FOR_SALE
        : AD_UNIT_PAGES.TO_RENT;

    const adUnitPageLSRP = isForSaleSearchFromRouter
        ? AD_UNIT_PAGES.FOR_SALE_LSRP
        : AD_UNIT_PAGES.TO_RENT_LSRP;

    return (
        <>
            <AdvertProvider value={adProviderVal}>
                <StickyQuickSearchBarDynamicShadow>
                    <StickyQuickSearchBarWrapper>
                        <StyledMobileQuickSearchBar>
                            <LocationWrapper>
                                <MobileQuickSearchButton
                                    openModal={openModalType}
                                />
                            </LocationWrapper>
                            <FiltersModal
                                version={LayoutVersion.Mobile}
                                testId="mobile-sub-header_filters-button"
                            />
                        </StyledMobileQuickSearchBar>
                    </StickyQuickSearchBarWrapper>
                </StickyQuickSearchBarDynamicShadow>
                <AlertPanelMobile links={links} />
                <DesktopSubHeaderWrapper
                    data-testid="search-desktop-subheader"
                    id="main-content"
                >
                    <SearchInputWrapper>
                        <SearchInitiation searchType={searchType} />
                    </SearchInputWrapper>
                </DesktopSubHeaderWrapper>
                <SearchResultsWrapper suppressHydrationWarning={true}>
                    <InnerWrapper>
                        <Main data-testid="search-content">{children}</Main>
                        <SideBar data-testid="search-sidebar">
                            <AlertPanel links={links} />
                            <AdvertSlotDfp
                                position={0}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[[300, 16]]}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.TEXT_LINK,
                                    adUnitPageLSRP
                                ]}
                            />
                            <AdvertSlotDfp
                                position={0}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[[300, 250]]}
                                adUnitCode={[AD_UNIT_ROOTS.MPU, adUnitPage]}
                            />
                            <AdvertSlotDfp
                                position={0}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[
                                    [300, 160],
                                    [300, 50],
                                    [300, 100]
                                ]}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.PARTNERSHIP,
                                    adUnitPage
                                ]}
                            />
                            <AdvertSlotDfp
                                position={0}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[[300, 120], 'fluid']}
                                adUnitCode={DFP_AD_UNIT.VALUATION_BOOSTER}
                            />
                            <AdvertSlotDfp
                                position={1}
                                showOnly={['>', breakpoints.lsrpSidebar]}
                                sizes={[
                                    [300, 160],
                                    [300, 50],
                                    [300, 100]
                                ]}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.PARTNERSHIP,
                                    adUnitPage
                                ]}
                            />

                            {!!seoAccordions.length && <SeoLinksComponent />}

                            <Sticky>
                                <AdvertSlotDfp
                                    position={0}
                                    showOnly={['>', breakpoints.lsrpSidebar]}
                                    sizes={[
                                        [120, 600],
                                        [160, 600],
                                        [300, 600]
                                    ]}
                                    adUnitCode={[
                                        AD_UNIT_ROOTS.SKY_SCRAPER,
                                        adUnitPage
                                    ]}
                                />
                            </Sticky>
                        </SideBar>
                    </InnerWrapper>
                </SearchResultsWrapper>
                <AlertCtaModal links={links} />
            </AdvertProvider>
            <ModalManager
                modalType={modalType}
                modalProps={{
                    updateResultsText: mobileModalUpdateResultsText,
                    canTabBetweenTypes: false,
                    isForSaleSearch,
                    searchType
                }}
                isModalShowing={isModalShowing}
                closeModal={closeModalType}
            />
        </>
    );
};

export default SearchResultsLayout;
